import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Paper, Typography } from "@material-ui/core";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

class PortfolioFaultFrequencyChartByCountry extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartOptions: {
        chart: {
          type: 'pie',
          backgroundColor: null,
          animation: {
            duration: 800
          }
        },
        title: {
          text: ''
        },
        tooltip: {
          pointFormat: '{series.name}: <b>{point.y}</b>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            size: '80%', 
            center: ['50%', '50%'], 
            animation: {
              duration: 800,
              easing: 'easeOutBounce'
            },
            dataLabels: {
              enabled: true,
              useHTML: true,
              distance: 20, 
              style: {
                color: 'black',
                textOverflow: 'ellipsis'
              }
            },
            showInLegend: false,
            states: {
              hover: {
                brightness: 0.1,
                animation: {
                  duration: 150
                }
              }
            }
          }
        },
        responsive: {
          rules: [{
            condition: {
              maxWidth: 500
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  size: '70%',
                  dataLabels: {
                    distance: 10
                  }
                }
              }
            }
          }]
        },
        series: [{
          name: 'Total Faults',
          colorByPoint: true,
          animation: {
            duration: 800,
            easing: 'easeOutCubic' 
          },
          data: []
        }],
        credits: {
          enabled: false
        },
        exporting: {
            enabled: false
        }
      }
    };
  }

  componentDidMount() {
    this.processData();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.portfolioFaultData !== this.props.portfolioFaultData ||
      prevProps.portfolioFaultDetails !== this.props.portfolioFaultDetails
    ) {
      this.processData();
    }
  }

  processData = () => {
    const { 
      portfolioFaultData, 
      portfolioFaultDetails,
      portfolioFaultLogData,
      selectedPortfolioFaultStations 
    } = this.props;

    // Check if single station 
    const isSingleStationMode = selectedPortfolioFaultStations?.length === 1;
    if (isSingleStationMode) {
      // Device-based view for single station
      if (!portfolioFaultLogData || portfolioFaultLogData.length === 0) {
        this.setState({
          chartOptions: {
            ...this.state.chartOptions,
            series: [{
              ...this.state.chartOptions.series[0],
              data: []
            }]
          }
        });
        return;
      }
      const deviceMap = {}; 

      portfolioFaultLogData.forEach(fault => {
        fault.deviceMaps?.forEach(device => {
          const deviceName = device.deviceName || 'Unknown Device';
          if (!deviceMap[deviceName]) {
            deviceMap[deviceName] = 0;
          }
          deviceMap[deviceName]++;
        });
      });

      // Prepare Highcharts data for devices and sort them by numeric suffix
      const chartData = Object.entries(deviceMap)
        .map(([deviceName, count]) => ({
          name: deviceName,
          y: count
        }))
        .sort((a, b) => {
          const numA = parseInt(a.name.replace(/[^\d]/g, ''), 10);
          const numB = parseInt(b.name.replace(/[^\d]/g, ''), 10);
          return numA - numB;
      });

      // Update chart with simplified device labels
      this.setState({
        chartOptions: {
          ...this.state.chartOptions,
          plotOptions: {
            pie: {
              ...this.state.chartOptions.plotOptions.pie,
              dataLabels: {
                ...this.state.chartOptions.plotOptions.pie.dataLabels,
                formatter: function() {
                  return `${this.point.name}`;
                }
              }
            }
          },
          series: [{
            name: 'Device Faults',
            data: chartData
          }]
        }
      });

    } else {
      // Original country-based view for multiple stations
      if (!portfolioFaultData || !portfolioFaultDetails.stationDetails || portfolioFaultData.length === 0) {
        this.setState({
          chartOptions: {
            ...this.state.chartOptions,
            series: [{
              ...this.state.chartOptions.series[0],
              data: []
            }]
          }
        });
        return;
      }
      const countryMap = {};  

      portfolioFaultData.forEach(fault => {
        const { stationId, faultTypes } = fault;
        const stationInfo = portfolioFaultDetails.stationDetails.find(
          s => s.name === stationId
        );

        if (stationInfo) {
          const country = stationInfo.country || 'Unknown';
          const project = stationInfo.project || 'Unknown';

          if (!countryMap[country]) {
            countryMap[country] = {
              totalFaults: 0,
              projects: new Set()
            };
          }

          // Sum up all fault counts (can be Warning, Critical, etc.)
          faultTypes.forEach(ft => {
            countryMap[country].totalFaults += ft.count || 0;
          });

          countryMap[country].projects.add(project);
        }
      });

      // Prepare Highcharts data for countries
      const chartData = Object.entries(countryMap).map(([country, data]) => ({
        name: country,
        country: country,
        projects: Array.from(data.projects),
        y: data.totalFaults
      }));

      // Update chart with country data
      this.setState({
        chartOptions: {
          ...this.state.chartOptions,
          plotOptions: {
            pie: {
              ...this.state.chartOptions.plotOptions.pie,
              dataLabels: {
                ...this.state.chartOptions.plotOptions.pie.dataLabels,
                formatter: function() {
                  return `<b>${this.point.country}</b>: ${this.point.y}<br/>` +
                         `Project: ${this.point.projects.join(', ')}`;
                }
              }
            }
          },
          series: [{
            name: 'Total Faults',
            data: chartData
          }]
        }});
    }
  };

  render() {
    const { chartOptions } = this.state;
    const { selectedPortfolioFaultStations } = this.props;
    const hasData = chartOptions.series[0].data.length > 0;
    const isSingleStationMode = selectedPortfolioFaultStations?.length === 1;
    const chartTitle = isSingleStationMode
      ? 'Fault Distribution by Inverter'
      : 'Fault Distribution by Country';
      
    return (
      <Paper className="portfolioFaultPie">
        <Typography className="portfolioFaultTitlePie" variant="h6" color="primary">
        {chartTitle}

        </Typography>
          {!selectedPortfolioFaultStations || selectedPortfolioFaultStations.length === 0 ? (
            <Typography variant="subtitle1" style={{ margin: '20px' }}>
               Please select at least one station to view the chart.
            </Typography>
          ) : hasData ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
            />
          ) : (
            <Typography variant="subtitle1" style={{ margin: '20px' }}>
              Data not available. Please select filters to see the chart.
            </Typography>
          )}
      </Paper>
    );
  }
}

const mapStateToProps = (state) => ({
  portfolioFaultData: state.portfolioFaultOverview?.portfolioFaultQueryData?.portfolioFaultData || [],
  portfolioFaultDetails: state.portfolioFaultOverview?.portfolioFaultDetails || {},
  portfolioFaultLogData: state.portfolioFaultOverview?.portfolioFaultLogData || [],
  selectedPortfolioFaultStations: state.portfolioFaultOverview?.selectedPortfolioFaultStations || []
});

export default connect(mapStateToProps)(PortfolioFaultFrequencyChartByCountry);
